import React, { useCallback, useRef, useState } from 'react';
import Konva from 'konva';
import { Group, Layer, Rect, Stage } from 'react-konva';
import { useContextMenu } from 'react-contexify';

import { TOOLS, isLineShape } from '../config';
import { ELEMENT_NODE_PREFIX, ELEMENT_TYPES, getElementNodeId } from '../core/documentSchema';
import { clampZoom } from '../core/viewport';
import ImageElement from '../elements/image/ImageElement';
import TextElement from '../elements/text/TextElement';
import ShapeElement from '../elements/shape/ShapeElement';
import TextEditOverlay from '../elements/text/TextEditOverlay';
import MediaStudioCropOverlay from '../components/media-studio-crop-overlay/MediaStudioCropOverlay';
import { MEDIA_STUDIO_LAYER_MENU_ID } from '../components/media-studio-layer-context-menu/MediaStudioLayerContextMenu';
import useCanvasPan from '../hooks/useCanvasPan';
import useCanvasPinch from '../hooks/useCanvasPinch';
import useMarqueeSelection from '../hooks/useMarqueeSelection';
import { isAdditiveSelectEvent } from '../utils/isAdditiveSelectEvent';
import { getElementsBox, snapLayerPosition, snapResizePoint } from '../utils/snap';
import GridOverlay from './GridOverlay';
import GuidesLayer from './GuidesLayer';
import SelectionTransformer from './SelectionTransformer';
import { applyTextWidthFromTransform, bakeImageTransformEnd } from './transformMath';

import { useMediaStudio } from '../context/MediaStudioContext';

import scss from './MediaStudioCanvas.module.scss';

const getTargetElementId = (target) => {
  const node = target.findAncestor((item) => item.id().startsWith(ELEMENT_NODE_PREFIX), true);
  return node ? node.id().slice(ELEMENT_NODE_PREFIX.length) : null;
};

function MediaStudioCanvas() {
  const {
    config,
    features,
    editor,
    viewport: viewportApi,
    stageRef,
    textEditing,
    selectedElement,
    themeColors,
  } = useMediaStudio();
  const {
    elements,
    width: docWidth,
    height: docHeight,
    background,
    selectedId,
    selectedIds,
    tool,
    cropRect,
    cropRatio,
    viewport,
    ui,
  } = editor;
  const containerSize = viewportApi.containerSize;
  const gridEnabled = features.grid && ui.gridEnabled;

  const [guides, setGuides] = useState([]);
  const { editingId } = textEditing;
  const elementsRef = useRef(elements);
  elementsRef.current = elements;
  const updateElementRef = useRef(editor.updateElement);
  updateElementRef.current = editor.updateElement;
  const { show } = useContextMenu({ id: MEDIA_STUDIO_LAYER_MENU_ID });
  const groupDragRef = useRef(null);

  const isCropMode = tool === TOOLS.CROP;
  const isHandTool = tool === TOOLS.HAND;
  const { zoom } = viewport;
  const editingElement = elements.find((el) => el.id === editingId) || null;
  const multiSelect = Boolean(features.multiSelect);
  const isGroupMember = (elementId) => selectedIds.length > 1 && selectedIds.includes(elementId);

  const { cursor, handlePointerDown, handlePointerMove, handlePointerUp } = useCanvasPan({
    isHandTool,
    isCropMode,
    editingId,
    multiSelect,
    onSelectElement: editor.selectElement,
    onPanBy: viewportApi.panBy,
  });

  const handleMarqueeSelect = (box, { additive }) => {
    const stage = stageRef.current;
    if (!stage) {
      return;
    }
    const current = elementsRef.current;
    const hits = current
      .filter((element) => element.visible && !element.locked)
      .filter((element) => {
        const node = stage.findOne(`#${getElementNodeId(element.id)}`);
        return node && Konva.Util.haveIntersection(box, node.getClientRect());
      })
      .map((element) => element.id);
    const unlocked = new Set(current.filter((element) => !element.locked).map((element) => element.id));
    const base = additive ? selectedIds.filter((id) => unlocked.has(id)) : [];
    const ids = [...new Set([...base, ...hits])];
    if (ids.length) {
      editor.selectElements(ids, hits[hits.length - 1]);
    } else if (!additive) {
      editor.selectElement(null);
    }
  };

  const { marquee, startMarquee, consumeClick } = useMarqueeSelection({ stageRef, onSelect: handleMarqueeSelect });

  const handleMouseDown = (event) => {
    if (handlePointerDown(event) || !multiSelect || isCropMode || isHandTool || event.evt.button !== 0) {
      return;
    }
    const isStage = event.target === event.target.getStage();
    const targetId = isStage ? null : getTargetElementId(event.target);
    const targetElement = targetId ? elements.find((element) => element.id === targetId) : null;
    if (isStage || targetElement?.locked) {
      startMarquee({ additive: isAdditiveSelectEvent(event.evt) });
    }
  };

  const { handlePinchMove, handlePinchEnd } = useCanvasPinch({
    stageRef,
    viewport,
    onViewportChange: viewportApi.setViewport,
  });

  const handleTouchStart = (event) => {
    if ((event.evt.touches?.length || 0) > 1) {
      handlePointerUp();
      return;
    }
    handlePointerDown(event);
  };

  const handleTouchMove = (event) => {
    if (!handlePinchMove(event)) {
      handlePointerMove(event);
    }
  };

  const handleTouchEnd = (event) => {
    handlePinchEnd(event);
    handlePointerUp();
  };

  const handleSelectElement = (elementId, event) => {
    if (consumeClick() || isCropMode || isHandTool) {
      return;
    }
    if (multiSelect && isAdditiveSelectEvent(event?.evt)) {
      editor.toggleSelection(elementId);
      return;
    }
    editor.selectElement(elementId);
  };

  const handleContextMenu = (element, event) => {
    if (!(features.transform || features.download) || isCropMode || isHandTool) {
      return;
    }
    event.evt.preventDefault();
    const inGroup = isGroupMember(element.id);
    if (!inGroup) {
      editor.selectElement(element.id);
    }
    show({
      event: event.evt,
      props: {
        layerId: element.id,
        layerIds: inGroup ? selectedIds : [element.id],
        locked: Boolean(element.locked),
      },
    });
  };

  const handleDragStart = (elementId) => {
    if (!isGroupMember(elementId) || groupDragRef.current) {
      return;
    }
    const stage = stageRef.current;
    const parent = stage?.findOne(`#${getElementNodeId(elementId)}`)?.getParent();
    const members = elements.filter((element) => selectedIds.includes(element.id));
    groupDragRef.current = {
      parent,
      pointer: parent?.getRelativePointerPosition(),
      box: getElementsBox(members),
      nodes: members
        .map((element) => ({ element, node: stage?.findOne(`#${getElementNodeId(element.id)}`) }))
        .filter(({ node }) => node),
    };
    setGuides([]);
  };

  const handleGroupDragMove = () => {
    const { parent, pointer: start, box, nodes } = groupDragRef.current;
    const pointer = parent?.getRelativePointerPosition();
    if (!pointer || !start || !box) {
      return;
    }
    const snapped = snapLayerPosition({
      x: box.x + pointer.x - start.x,
      y: box.y + pointer.y - start.y,
      width: box.width,
      height: box.height,
      stageWidth: docWidth,
      stageHeight: docHeight,
      zoom,
      otherElements: ui.snapToObjects ? elements.filter((el) => !selectedIds.includes(el.id) && el.visible) : [],
    });
    nodes.forEach(({ element, node }) =>
      node.position({ x: element.x + snapped.x - box.x, y: element.y + snapped.y - box.y }),
    );
    setGuides(snapped.guides);
  };

  const handleDragMove = (element, event) => {
    if (!features.guides || isCropMode) {
      return;
    }

    if (groupDragRef.current) {
      handleGroupDragMove();
      return;
    }

    const node = event.target;
    const snapped = snapLayerPosition({
      x: node.x(),
      y: node.y(),
      width: element.width,
      height: element.height,
      stageWidth: docWidth,
      stageHeight: docHeight,
      zoom,
      otherElements: ui.snapToObjects ? elements.filter((el) => el.id !== element.id && el.visible) : [],
    });

    node.position({ x: snapped.x, y: snapped.y });
    setGuides(snapped.guides);
  };

  const handleSnapResize = (point) => {
    const snapped = snapResizePoint({
      ...point,
      stageWidth: docWidth,
      stageHeight: docHeight,
      zoom,
      otherElements: ui.snapToObjects ? elements.filter((el) => el.id !== selectedId && el.visible) : [],
    });
    setGuides(snapped.guides);
    return { x: snapped.x, y: snapped.y };
  };

  const handleDragEnd = (elementId, event) => {
    setGuides([]);
    if (isGroupMember(elementId)) {
      if (!groupDragRef.current) {
        return;
      }
      groupDragRef.current = null;
      const stage = stageRef.current;
      const updates = selectedIds
        .map((id) => ({ id, node: stage?.findOne(`#${getElementNodeId(id)}`) }))
        .filter(({ node }) => node)
        .map(({ id, node }) => ({ id, changes: { x: node.x(), y: node.y() } }));
      editor.changeElements(updates);
      return;
    }
    editor.change(elementId, {
      x: event.target.x(),
      y: event.target.y(),
    });
  };

  const handleTextTransform = (elementId, event) => {
    const node = event.target;
    const element = elements.find((item) => item.id === elementId);
    if (!element || element.type !== ELEMENT_TYPES.TEXT) {
      return;
    }
    applyTextWidthFromTransform(node, element);
  };

  const handleTransformEnd = (elementId, event) => {
    setGuides([]);
    const node = event.target;
    const element = elements.find((item) => item.id === elementId);

    if (element?.type === ELEMENT_TYPES.TEXT) {
      editor.change(elementId, applyTextWidthFromTransform(node, element));
      return;
    }

    editor.change(elementId, bakeImageTransformEnd(node, element, { minHeight: isLineShape(element) ? 1 : 10 }));
  };

  const handleWheel = (event) => {
    event.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage || !viewportApi.setViewport || editingId) {
      return;
    }

    const pointer = stage.getPointerPosition();
    if (!pointer) {
      return;
    }

    const scaleBy = 1.06;
    const direction = event.evt.deltaY > 0 ? -1 : 1;
    const oldZoom = viewport.zoom || 1;
    const newZoom = clampZoom(direction > 0 ? oldZoom * scaleBy : oldZoom / scaleBy);
    const mousePointTo = {
      x: (pointer.x - viewport.x) / oldZoom,
      y: (pointer.y - viewport.y) / oldZoom,
    };

    viewportApi.setViewport({
      zoom: newZoom,
      x: pointer.x - mousePointTo.x * newZoom,
      y: pointer.y - mousePointTo.y * newZoom,
    });
  };

  const handleTextDblClick = (element, event) => {
    if (isCropMode || isHandTool) {
      return;
    }
    if (event) {
      event.cancelBubble = true;
      event.evt?.preventDefault?.();
      event.evt?.stopPropagation?.();
    }
    textEditing.startEditing(element.id);
  };

  const handleTextMeasure = useCallback((id, { height }) => {
    const element = elementsRef.current.find((item) => item.id === id);
    if (!element) {
      return;
    }
    const shift = (height - element.height) / 2;
    const angle = (element.rotation * Math.PI) / 180;
    updateElementRef.current(id, {
      height,
      x: element.x - Math.sin(angle) * shift,
      y: element.y + Math.cos(angle) * shift,
    });
  }, []);

  if (!docWidth || !docHeight || !containerSize.width || !containerSize.height) {
    return null;
  }

  const interactive = !isCropMode && !isHandTool && !editingId;

  return (
    <div className={scss.host} style={{ cursor }} data-editing={editingId ? 'true' : 'false'}>
      <Stage
        ref={stageRef}
        width={containerSize.width}
        height={containerSize.height}
        scaleX={zoom}
        scaleY={zoom}
        x={viewport.x}
        y={viewport.y}
        listening={!editingId}
        onWheel={editingId ? undefined : handleWheel}
        onMouseDown={editingId ? undefined : handleMouseDown}
        onMouseMove={editingId ? undefined : handlePointerMove}
        onMouseUp={editingId ? undefined : handlePointerUp}
        onMouseLeave={editingId ? undefined : handlePointerUp}
        onTouchStart={editingId ? undefined : handleTouchStart}
        onTouchMove={editingId ? undefined : handleTouchMove}
        onTouchEnd={editingId ? undefined : handleTouchEnd}>
        <Layer>
          <Group clipX={0} clipY={0} clipWidth={docWidth} clipHeight={docHeight}>
            <Rect
              x={0}
              y={0}
              width={docWidth}
              height={docHeight}
              fill={background?.color || '#ffffff'}
              listening={false}
            />
            {elements.map((element) => {
              if (element.type === ELEMENT_TYPES.TEXT) {
                return (
                  <TextElement
                    key={element.id}
                    element={element}
                    hidden={editingId === element.id}
                    interactive={interactive && !element.locked}
                    listening={!isHandTool && !isCropMode}
                    onSelect={handleSelectElement}
                    onContextMenu={handleContextMenu}
                    onDragStart={handleDragStart}
                    onDragMove={handleDragMove}
                    onDragEnd={handleDragEnd}
                    onTransform={handleTextTransform}
                    onTransformEnd={handleTransformEnd}
                    onDblClick={handleTextDblClick}
                    onMeasure={handleTextMeasure}
                  />
                );
              }

              if (element.type === ELEMENT_TYPES.SHAPE) {
                return (
                  <ShapeElement
                    key={element.id}
                    element={element}
                    interactive={interactive && !element.locked}
                    listening={!isHandTool && !isCropMode}
                    onSelect={handleSelectElement}
                    onContextMenu={handleContextMenu}
                    onDragStart={handleDragStart}
                    onDragMove={handleDragMove}
                    onDragEnd={handleDragEnd}
                    onTransformEnd={handleTransformEnd}
                  />
                );
              }

              return (
                <ImageElement
                  key={element.id}
                  element={element}
                  interactive={interactive && !element.locked}
                  listening={!isHandTool && !isCropMode}
                  onSelect={handleSelectElement}
                  onContextMenu={handleContextMenu}
                  onDragStart={handleDragStart}
                  onDragMove={handleDragMove}
                  onDragEnd={handleDragEnd}
                  onTransformEnd={handleTransformEnd}
                />
              );
            })}
          </Group>
        </Layer>

        {gridEnabled ? (
          <Layer listening={false}>
            <GridOverlay width={docWidth} height={docHeight} gridSize={config.gridSize} zoom={zoom} />
          </Layer>
        ) : null}

        <GuidesLayer guides={guides} docWidth={docWidth} docHeight={docHeight} zoom={zoom} />

        {marquee ? (
          <Layer listening={false}>
            <Rect
              x={marquee.x}
              y={marquee.y}
              width={marquee.width}
              height={marquee.height}
              fill={themeColors.accent}
              opacity={0.12}
            />
            <Rect
              x={marquee.x}
              y={marquee.y}
              width={marquee.width}
              height={marquee.height}
              stroke={themeColors.accent}
              strokeWidth={1 / zoom}
              dash={[4 / zoom, 4 / zoom]}
            />
          </Layer>
        ) : null}

        {isCropMode ? (
          <Layer>
            <MediaStudioCropOverlay
              cropRect={cropRect}
              cropRatio={cropRatio}
              stageWidth={docWidth}
              stageHeight={docHeight}
              zoom={zoom}
              onChange={editor.setCropRect}
            />
          </Layer>
        ) : (
          <SelectionTransformer
            stageRef={stageRef}
            selectedId={selectedId}
            selectedIds={selectedIds}
            selectedElement={selectedElement}
            elements={elements}
            zoom={zoom}
            enabled={!isHandTool && !editingId}
            onSnapResize={features.guides ? handleSnapResize : undefined}
          />
        )}
      </Stage>

      {editingElement ? (
        <TextEditOverlay
          key={editingElement.id}
          element={editingElement}
          viewport={viewport}
          selection={textEditing.selection}
          onSelectionChange={textEditing.setSelection}
          onRunsChange={textEditing.updateRuns}
          onToggleStyle={textEditing.toggleStyle}
          onUndo={editor.undo}
          onRedo={editor.redo}
          onFinish={textEditing.finishEditing}
        />
      ) : null}
    </div>
  );
}

export default MediaStudioCanvas;
